import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button, Stack, Container, useTheme, Paper, alpha, IconButton, Grid, Chip } from "@mui/material";
import { motion, useAnimation, useScroll, useTransform, AnimatePresence } from "framer-motion";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';
import DiamondIcon from '@mui/icons-material/Diamond';
import SecurityIcon from '@mui/icons-material/Security';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import { Link } from "react-router-dom";

const Hero = () => {
  const theme = useTheme();
  const controls = useAnimation();
  const { scrollY } = useScroll();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [showAustralianLiberty, setShowAustralianLiberty] = useState(false);
  const containerRef = useRef(null);

  // Parallax effects based on scroll
  const y1 = useTransform(scrollY, [0, 500], [0, -150]);
  const y2 = useTransform(scrollY, [0, 500], [0, -100]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.7]);

  // Mouse move parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX - window.innerWidth / 2) * 0.02,
        y: (e.clientY - window.innerHeight / 2) * 0.02,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    setIsLoaded(true);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Auto-flip effect
  useEffect(() => {
    const flipInterval = setInterval(() => {
      setShowAustralianLiberty(prev => !prev);
    }, 4000);

    return () => clearInterval(flipInterval);
  }, []);

  const handleCoinClick = () => {
    setShowAustralianLiberty(!showAustralianLiberty);
  };

  // Premium featured coin with Australian diamond coin
  const featuredCoin = {
    id: 107,
    name: "Australian Diamond Coin - Treasure of Australia",
    year: "2007",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$5,000 – $20,000",
    priceRange: { min: 5000, max: 20000 },
    image: "/assets/images/jp/Australian daimond coin treasure of australia.jpeg",
    flipImage: "/assets/images/jp/top hero image.png",
    certification: "PCGS",
    metal: "Gold",
    hasDiamond: true,
    mintage: 500,
    diameter: "38.61mm",
    weight: "31.1g",
    rarity: "Extremely Rare"
  };

  const stats = [
    { value: "100%", label: "Authenticity Guaranteed", icon: ShieldIcon, color: "#C6A75E" },
  ];

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        height: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        margin: 0,
        padding: 0,
        backgroundImage: `url('/assets/images/jp/bg_image.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backdropFilter: 'blur(1px)',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1,
        },
      }}
    >
      {/* Animated gradient overlay */}
      <Box
        component={motion.div}
        animate={{
          background: [
            'radial-gradient(circle at 20% 30%, rgba(198,167,94,0.15) 0%, transparent 70%)',
            'radial-gradient(circle at 80% 70%, rgba(198,167,94,0.2) 0%, transparent 70%)',
            'radial-gradient(circle at 40% 50%, rgba(198,167,94,0.15) 0%, transparent 70%)',
          ]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      {/* Enhanced particle system */}
      {[...Array(60)].map((_, i) => (
        <Box
          key={i}
          component={motion.div}
          initial={{ 
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000), 
            y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
            scale: Math.random() * 0.5 + 0.1
          }}
          animate={{
            y: [null, -40, 40, -40],
            x: [null, 40, -40, 40],
            opacity: [0.03, 0.12, 0.03],
          }}
          transition={{
            duration: Math.random() * 20 + 15,
            repeat: Infinity,
            ease: "linear",
          }}
          sx={{
            position: 'absolute',
            width: Math.random() * 4 + 1,
            height: Math.random() * 4 + 1,
            borderRadius: '50%',
            background: i % 4 === 0 ? '#C6A75E' : '#FFFFFF',
            filter: 'blur(2px)',
            opacity: 0.08,
            zIndex: 2,
          }}
        />
      ))}

      {/* Animated gold accent lines */}
      <Box
        component={motion.div}
        style={{ y: y1, opacity: opacity }}
        sx={{
          position: 'absolute',
          top: '10%',
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(198, 167, 94, 0.6), #C6A75E, rgba(198, 167, 94, 0.6), transparent)',
          zIndex: 2,
        }}
      />
      <Box
        component={motion.div}
        style={{ y: y2, opacity: opacity }}
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(198, 167, 94, 0.5), #C6A75E, rgba(198, 167, 94, 0.5), transparent)',
          zIndex: 2,
        }}
      />

      {/* Elegant Corner Decorations with animation */}
      {[
        { top: 40, left: 40, borderRight: false, borderBottom: false },
        { top: 40, right: 40, borderLeft: false, borderBottom: false },
        { bottom: 40, left: 40, borderTop: false, borderRight: false },
        { bottom: 40, right: 40, borderTop: false, borderLeft: false },
      ].map((corner, index) => (
        <Box
          key={index}
          component={motion.div}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1, duration: 0.6 }}
          sx={{
            position: 'absolute',
            width: 100,
            height: 100,
            ...corner,
            borderTop: corner.borderTop !== false ? '2px solid' : 'none',
            borderRight: corner.borderRight !== false ? '2px solid' : 'none',
            borderBottom: corner.borderBottom !== false ? '2px solid' : 'none',
            borderLeft: corner.borderLeft !== false ? '2px solid' : 'none',
            borderColor: alpha('#C6A75E', 0.5),
            zIndex: 2,
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 20,
              height: 20,
              borderTop: corner.borderTop !== false ? '2px solid #C6A75E' : 'none',
              borderRight: corner.borderRight !== false ? '2px solid #C6A75E' : 'none',
              ...(corner.top && { top: 15, right: 15 }),
              ...(corner.bottom && { bottom: 15, left: 15 }),
            }
          }}
        />
      ))}

      <Container 
        maxWidth={false} 
        sx={{ 
          position: 'relative', 
          zIndex: 3,
          width: '100%',
          maxWidth: '1400px !important',
          margin: '0 auto',
          px: { xs: 4, sm: 5, md: 6 },
        }}
      >
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={{ xs: 4, md: 6 }}
          alignItems="center"
          sx={{ 
            minHeight: '80vh',
            width: '100%',
          }}
        >
          {/* Left Content - Enhanced */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
            style={{ flex: 1, width: '100%' }}
          >
            {/* Prestige Badge with animation */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <motion.div
                animate={{ width: [40, 70, 40] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 2,
                    background: 'linear-gradient(90deg, #C6A75E, transparent)',
                  }}
                />
              </motion.div>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: '#C6A75E',
                  letterSpacing: '0.4em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                EST. 2005
              </Typography>
            </Stack>
            
            <Typography 
              variant="h1" 
              sx={{ 
                mb: 2,
                fontWeight: 800,
                lineHeight: 1.2,
                color: '#FFFFFF',
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.8rem', lg: '4.5rem' },
                fontFamily: '"Cormorant Garamond", serif',
                textShadow: '0 4px 12px rgba(0,0,0,0.4)',
              }}
            >
              Where
              <motion.span
                whileHover={{ scale: 1.05 }}
                style={{ display: 'inline-block', marginLeft: '12px', marginRight: '12px' }}
              >
                <Box component="span" sx={{ 
                  color: '#C6A75E',
                  fontWeight: 800,
                  position: 'relative',
                  textShadow: '0 2px 8px rgba(198,167,94,0.5)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: 8,
                    left: 0,
                    right: 0,
                    height: '12px',
                    background: alpha('#C6A75E', 0.3),
                    zIndex: -1,
                  }
                }}>
                  Rare Coins
                </Box>
              </motion.span>
            </Typography>
            
            <Typography 
              variant="h1" 
              sx={{ 
                mb: 3,
                fontWeight: 800,
                lineHeight: 1.2,
                color: '#FFFFFF',
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.8rem', lg: '4.5rem' },
                fontFamily: '"Cormorant Garamond", serif',
                textShadow: '0 4px 12px rgba(0,0,0,0.4)',
              }}
            >
              Meet
              <motion.span
                whileHover={{ scale: 1.02 }}
                style={{ display: 'inline-block', marginLeft: '12px' }}
              >
                <Box component="span" sx={{ 
                  color: '#C6A75E',
                  display: 'inline-block',
                  fontWeight: 800,
                  borderBottom: '3px solid',
                  borderBottomColor: '#C6A75E',
                  pb: 0.5,
                  textShadow: '0 2px 8px rgba(198,167,94,0.5)',
                }}>
                  Timeless Value
                </Box>
              </motion.span>
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 5,
                color: 'rgba(255,255,255,0.95)',
                fontWeight: 400,
                lineHeight: 1.7,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                maxWidth: '600px',
                fontFamily: '"Montserrat", sans-serif',
                textShadow: '0 1px 3px rgba(0,0,0,0.4)',
              }}
            >
              Experience the pinnacle of numismatic excellence. 
              Each piece in our collection represents the perfect harmony 
              of historical significance and investment-grade rarity.
            </Typography>
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={3}
              sx={{ mb: 6 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Button 
                  variant="contained" 
                  size="large"
                  component={Link}
                  to="/collection"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    px: { xs: 5, md: 6 }, 
                    py: { xs: 1.5, md: 1.8 },
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    background: 'linear-gradient(135deg, #C6A75E 0%, #B89A4F 50%, #C6A75E 100%)',
                    color: '#000000',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 10px 30px -10px rgba(198,167,94,0.6)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #D4B86A 0%, #C6A75E 50%, #D4B86A 100%)',
                      boxShadow: '0 15px 40px -10px rgba(198,167,94,0.8)',
                    },
                  }}
                >
                  Explore Collection
                </Button>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Button 
                  variant="outlined" 
                  size="large"
                  component={Link}
                  to="/sell"
                  endIcon={<PlayArrowIcon />}
                  sx={{ 
                    px: { xs: 5, md: 6 }, 
                    py: { xs: 1.5, md: 1.8 },
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    borderColor: '#FFFFFF',
                    color: '#FFFFFF',
                    borderWidth: '2px',
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    '&:hover': {
                      borderColor: '#C6A75E',
                      backgroundColor: alpha('#C6A75E', 0.2),
                    },
                  }}
                >
                  Sell Your Coins
                </Button>
              </motion.div>
            </Stack>

            {/* Enhanced Trust Indicators */}
            <Stack 
              direction="row" 
              spacing={4}
              sx={{ 
                flexWrap: 'wrap',
                gap: 3,
              }}
            >
              {stats.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${alpha(item.color, 0.2)} 0%, ${alpha(item.color, 0.05)} 100%)`,
                          backdropFilter: 'blur(8px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${alpha(item.color, 0.3)}`,
                        }}
                      >
                        <IconComponent sx={{ color: item.color, fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}>
                          {item.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.7rem', lineHeight: 1.2 }}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Stack>
                  </motion.div>
                );
              })}
            </Stack>
          </motion.div>

          {/* Right Content - Coin Display with Auto Flip */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            style={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                maxWidth: '450px',
                margin: '0 auto',
                cursor: 'pointer',
              }}
              onClick={handleCoinClick}
            >
              {/* Animated glow rings */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(198,167,94,0.4) 0%, transparent 70%)',
                  filter: 'blur(50px)',
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />

              {/* Rotating rings */}
              {[1, 2, 3, 4].map((ring, index) => (
                <motion.div
                  key={ring}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25 - index * 5, repeat: Infinity, ease: "linear" }}
                  style={{
                    position: 'absolute',
                    width: `${240 + index * 40}px`,
                    height: `${240 + index * 40}px`,
                    borderRadius: '50%',
                    border: `2px solid ${alpha('#C6A75E', 0.25 - index * 0.05)}`,
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                />
              ))}

              {/* Main Coin Container - Perfect Circle */}
              <Box
                sx={{
                  width: { xs: 280, sm: 330, md: 380 },
                  height: { xs: 280, sm: 330, md: 380 },
                  position: 'relative',
                  zIndex: 3,
                }}
              >
                <motion.div
                  key={showAustralianLiberty ? 'liberty' : 'australian'}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                  }}
                >
                  <Paper
                    elevation={24}
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '3px solid',
                      borderColor: '#C6A75E',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                      backgroundColor: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                      transition: 'transform 0.3s ease',
                    }}
                  >
                    <Box
                      component="img"
                      src={showAustralianLiberty ? featuredCoin.flipImage : featuredCoin.image}
                      alt={showAustralianLiberty ? "American Liberty Coin" : "Australian Diamond Coin"}
                      sx={{
                        width: '90%',
                        height: '90%',
                        objectFit: 'contain',
                        display: 'block',
                        margin: '0 auto',
                      }}
                      onError={(e) => {
                        e.target.src = showAustralianLiberty 
                          ? 'https://via.placeholder.com/380x380/C6A75E/FFFFFF?text=🇺🇸+American+Liberty'
                          : 'https://via.placeholder.com/380x380/C6A75E/FFFFFF?text=🇦🇺+Australian+Diamond+Coin';
                      }}
                    />
                    
                    {/* Diamond accent with pulse effect */}
                    {!showAustralianLiberty && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '8%',
                          right: '8%',
                          zIndex: 4,
                          pointerEvents: 'none',
                        }}
                      >
                        <motion.div
                          animate={{
                            scale: [1, 1.3, 1],
                            rotate: [0, 180, 360],
                          }}
                          transition={{
                            duration: 2.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        >
                          <DiamondIcon sx={{ 
                            color: '#C6A75E', 
                            fontSize: 32,
                            filter: 'drop-shadow(0 0 20px rgba(198,167,94,0.9))',
                          }} />
                        </motion.div>
                      </Box>
                    )}

                    {/* Australian flag accent */}
                    {showAustralianLiberty && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '8%',
                          right: '8%',
                          zIndex: 4,
                          pointerEvents: 'none',
                        }}
                      >
                        <motion.div
                          animate={{
                            scale: [1, 1.2, 1],
                            rotate: [0, 10, -10, 0],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        >
                          <Typography sx={{ fontSize: 28 }}>🇦🇺</Typography>
                        </motion.div>
                      </Box>
                    )}
                    
                    {/* Premium overlay gradient */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 30% 30%, rgba(198,167,94,0.1) 0%, transparent 70%)',
                        pointerEvents: 'none',
                      }}
                    />

                    {/* Top certification badge */}
                    <motion.div
                      animate={{ 
                        y: [0, -6, 0],
                      }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      style={{
                        position: 'absolute',
                        top: '5%',
                        left: '5%',
                        zIndex: 4,
                        pointerEvents: 'none',
                      }}
                    >
                      <Paper
                        sx={{
                          p: 1.2,
                          background: 'linear-gradient(135deg, rgba(198,167,94,0.95) 0%, rgba(184,154,79,0.95) 100%)',
                          color: '#000000',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                          backdropFilter: 'blur(5px)',
                          borderRadius: 2,
                        }}
                      >
                        {showAustralianLiberty ? (
                          <StarIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <VerifiedIcon sx={{ fontSize: 18 }} />
                        )}
                      </Paper>
                    </motion.div>
                  </Paper>
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        </Stack>
      </Container>

      {/* Enhanced Scroll Indicator */}
      <Box
        component={motion.div}
        animate={{ y: [0, 12, 0] }}
        transition={{ duration: 1.8, repeat: Infinity }}
        sx={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          cursor: 'pointer',
        }}
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
      >
        <Stack alignItems="center" spacing={1.5}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#FFFFFF',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              fontWeight: 500,
              textShadow: '0 1px 2px rgba(0,0,0,0.4)',
            }}
          >
            Scroll to Explore
          </Typography>
          <Box
            sx={{
              width: 28,
              height: 48,
              border: '2px solid',
              borderColor: alpha('#FFFFFF', 0.6),
              borderRadius: 20,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 4,
                height: 12,
                backgroundColor: '#C6A75E',
                borderRadius: 2,
                animation: 'scrollIndicator 2s infinite',
              }
            }}
          />
        </Stack>
      </Box>

      <style>
        {`
          @keyframes scrollIndicator {
            0% { opacity: 1; transform: translateX(-50%) translateY(0); }
            100% { opacity: 0; transform: translateX(-50%) translateY(20px); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px) translateX(0px); }
            50% { transform: translateY(-20px) translateX(10px); }
          }
        `}
      </style>
    </Box>
  );
};

export default Hero;